import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import {
  IMassUploadDiffDto,
  IMassUploadDiffRequestDto,
  IMetadataDifferences,
  Serialized,
} from 'types';
import { computed, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IUploadRecord } from '../MassUpload.types';

export type MetadataDiffByDocumentId = {
  [documentId: string]: IMetadataDifferences;
};
export type HasRenditionFileByDocumentId = { [documentId: string]: boolean };

export function useVersionsDiff(
  projectId: string,
  supplierId: string | undefined,
  uploadRecords: Ref<IUploadRecord[]>,
) {
  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const isGettingDiff = ref(false);
  const documentIdsOfPreviousVersions = ref<string[]>([]);
  const metadataChanges = ref<MetadataDiffByDocumentId>({});
  /** Previous version has a rendition file */
  const hasRenditionFileByDocumentId = ref<HasRenditionFileByDocumentId>({});

  /** Reacting only once when the CSV is added */
  watch(
    uploadRecords,
    async () => {
      await getVerionsDiff();
      setPreviousVersionFlags();
    },
    { deep: true, once: true },
  );

  async function getVerionsDiff(): Promise<void> {
    if (!uploadRecords.value.length) return;

    isGettingDiff.value = true;
    const body: Serialized<IMassUploadDiffRequestDto> = {
      projectId,
      supplierId,
      documents: uploadRecords.value.map((record) => ({
        documentId: record.metadata!.documentId,
        metadata: record.metadata,
      })),
    };
    try {
      const response = await axios.post<IMassUploadDiffDto>(
        endpointsList.uploadHandler.getVersionDiff(),
        body,
      );
      documentIdsOfPreviousVersions.value = response.data.existingDocuments.map(
        (doc) => doc.documentId,
      );
      metadataChanges.value = Object.fromEntries(
        response.data.metadataDifferences.map((change) => [
          change.documentId,
          change.differences,
        ]),
      );
      hasRenditionFileByDocumentId.value = Object.fromEntries(
        response.data.existingDocuments.map((doc) => [
          doc.documentId,
          doc.hasRenditionFile,
        ]),
      );
    } catch {
      createErrorToast(t('documentUpload.errors.cannotCheckPreviousVersions'));
    } finally {
      isGettingDiff.value = false;
    }
  }

  function setPreviousVersionFlags() {
    uploadRecords.value.forEach((record) => {
      if (
        documentIdsOfPreviousVersions.value.includes(
          record.metadata!.documentId,
        )
      ) {
        record.hasPreviousVersion = true;
      }
    });
  }

  /**
   * `documentId`s of records that had a previous version with a rendition file
   * and for which the added CSV does not contain a record for a rendition
   * file.
   */
  const recordsWithMissingReditionFile = computed<string[]>(() => {
    return uploadRecords.value
      .filter(
        (record) =>
          record.metadata?.documentId &&
          hasRenditionFileByDocumentId.value[record.metadata?.documentId] &&
          !record.renditionFile,
      )
      .map((record) => record.metadata!.documentId);
  });

  /**
   * At least one record is expected to have a rendition file (previous version
   * of this file has a rendition file) but the current CSV does not contain a
   * rendition file for it.
   */
  const isMissingRenditionRecordError = computed<boolean>(
    () => !!recordsWithMissingReditionFile.value.length,
  );

  return {
    isGettingDiff,
    metadataChanges,
    hasRenditionFileByDocumentId,
    recordsWithMissingReditionFile,
    isMissingRenditionRecordError,
  };
}
